/**
 * EngineDetail Component
 *
 * Composes EngineDetailStandard with engine-specific components.
 * Uses switch on engine type to render the appropriate specific component.
 */

import type {
  AsoEngineType,
  AsoAuditOutputHeader,
  SubengineOutput,
  UnifiedDetailedAnalysis,
  CompetitorDiscoveryEngineSpecificAnalysis,
  IconEngineSpecificAnalysis,
  IntentEngineSpecificAnalysis,
  AiDiscoveryEngineSpecificAnalysis,
  SearchTermEngineSpecificAnalysis,
  KeywordEngineSpecificAnalysis,
  ScreenshotEngineSpecificAnalysis,
  SentimentEngineSpecificAnalysis,
  StoretextEngineSpecificAnalysis,
  StoretextSemanticPreprocessingOutput,
  StoreSearchResultItem,
} from "@a7y/types";
import type { AuditOutputV2 } from "@a7y/audit-output-v2";
import { Subheading, Divider } from "@a7y/tailwind-catalyst";
import { EngineDetailStandard } from "./EngineDetailStandard";
import { EngineDetailCompetitor } from "./EngineDetailCompetitor";
import { EngineDetailIcon } from "./EngineDetailIcon";
import { EngineDetailKeyword } from "./EngineDetailKeyword";
import { EngineDetailScreenshot } from "./EngineDetailScreenshot";
import { EngineDetailSentiment } from "./EngineDetailSentiment";
import { EngineDetailStoretext } from "./EngineDetailStoretext";
import { EngineDetailIntent } from "./EngineDetailIntent";
import { EngineDetailAiDiscovery } from "./EngineDetailAiDiscovery";
import { EngineDetailSearchTerm } from "./EngineDetailSearchTerm";
import { StoretextUnavailableFallback } from "./StoretextUnavailableFallback";

export interface EngineDetailProps {
  /** Engine output with detailed analysis */
  output: SubengineOutput<unknown, unknown>;
  /** Engine type for rendering the specific component */
  engineType: AsoEngineType;
  /** Audit header (locale/storefront info) — threaded to engine-specific components that need it */
  header?: AsoAuditOutputHeader;
  /** Callback to fetch store search results (threaded to SearchTerm engine) */
  onSearch?: (params: {
    term: string;
    storefront: string;
    countryCode: string;
    languageCode?: string;
  }) => Promise<StoreSearchResultItem[]>;
  /** When true, force-expands all collapsible sections and removes truncation (for print/PDF) */
  printMode?: boolean;
  /**
   * Full audit payload — required for engine-specific print bodies that
   * compose multiple slices of `AuditOutputV2` (e.g. the v2 Screenshots
   * print section, which calls `extractScreenshotsViewModel(audit)`).
   * Optional for back-compat with legacy callers.
   */
  audit?: AuditOutputV2;
  /** Additional CSS classes */
  className?: string;
}

export function EngineDetail({ output, engineType, header, onSearch, printMode, audit, className = "" }: EngineDetailProps) {
  const { detail, engine_specific, intermediate } = output;

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Standard Details Section */}
      {!!detail && (
        <section>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1 h-5 rounded-full bg-indigo-500" />
            <Subheading level={3}>Standard Details</Subheading>
          </div>
          <EngineDetailStandard detail={detail as UnifiedDetailedAnalysis} printMode={printMode} />
        </section>
      )}

      {/* Engine-Specific Section */}
      {!!engine_specific && (
        <>
          <Divider soft />
          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1 h-5 rounded-full bg-purple-500" />
              <Subheading level={3}>Engine-Specific Analysis</Subheading>
            </div>
            {renderEngineSpecific(engineType, engine_specific, detail, intermediate, header, onSearch, printMode, audit)}
          </section>
        </>
      )}
    </div>
  );
}

type SearchFn = (params: { term: string; storefront: string; countryCode: string; languageCode?: string }) => Promise<StoreSearchResultItem[]>;

function renderEngineSpecific(engineType: AsoEngineType, engineSpecific: unknown, detail: UnifiedDetailedAnalysis, intermediate: unknown, header?: AsoAuditOutputHeader, onSearch?: SearchFn, printMode?: boolean, audit?: AuditOutputV2) {
  switch (engineType) {
    case "competitor":
      return (
        <EngineDetailCompetitor
          analysis={engineSpecific as CompetitorDiscoveryEngineSpecificAnalysis}
          printMode={printMode}
        />
      );

    case "icon":
      return (
        <EngineDetailIcon
          analysis={engineSpecific as IconEngineSpecificAnalysis}
          printMode={printMode}
        />
      );

    case "keyword":
    case "keywordService":
      return (
        <EngineDetailKeyword
          analysis={engineSpecific as KeywordEngineSpecificAnalysis}
          printMode={printMode}
        />
      );

    case "screenshot":
      return (
        <EngineDetailScreenshot
          analysis={engineSpecific as ScreenshotEngineSpecificAnalysis}
          detail={detail}
          printMode={printMode}
          audit={audit}
        />
      );

    case "sentiment":
      return (
        <EngineDetailSentiment
          analysis={engineSpecific as SentimentEngineSpecificAnalysis}
          detail={detail}
          printMode={printMode}
        />
      );

    case "storetext": {
      const storetextAnalysis = engineSpecific as StoretextEngineSpecificAnalysis | null | undefined;
      if (!storetextAnalysis || !storetextAnalysis.overall_analysis || !storetextAnalysis.title_analysis) {
        return <StoretextUnavailableFallback />;
      }
      const semanticPreprocessing = (intermediate as { semantic_preprocessing?: StoretextSemanticPreprocessingOutput } | undefined)?.semantic_preprocessing ?? undefined;
      return (
        <EngineDetailStoretext
          analysis={storetextAnalysis}
          detail={detail}
          semanticPreprocessing={semanticPreprocessing}
          printMode={printMode}
        />
      );
    }

    case "intent":
      return (
        <EngineDetailIntent
          analysis={engineSpecific as IntentEngineSpecificAnalysis}
          detail={detail}
          printMode={printMode}
        />
      );

    case "aiDiscovery":
      return (
        <EngineDetailAiDiscovery
          analysis={engineSpecific as AiDiscoveryEngineSpecificAnalysis}
          detail={detail}
          printMode={printMode}
        />
      );

    case "searchTerm":
      return (
        <EngineDetailSearchTerm
          analysis={engineSpecific as SearchTermEngineSpecificAnalysis}
          countryCode={header?.country_code}
          languageCode={header?.language_code}
          onSearch={onSearch}
          printMode={printMode}
        />
      );

    case "audit":
      // Audit engine doesn't have engine-specific detail rendering here
      // It uses the full-audit components instead
      return null;

    default:
      // For unknown engine types, just show raw JSON
      return (
        <div className="rounded-lg border border-zinc-950/10 dark:border-white/10 overflow-hidden">
          <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-950/10 dark:border-white/10">
            <Subheading level={4}>Engine-Specific Data</Subheading>
          </div>
          <div className="p-4">
            <pre className="text-xs bg-zinc-900 dark:bg-zinc-950 p-4 rounded-lg overflow-auto text-zinc-100 max-h-96">
              <code>{JSON.stringify(engineSpecific, null, 2)}</code>
            </pre>
          </div>
        </div>
      );
  }
}
